import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import {
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  EyeOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
} from "@ant-design/icons";
import { uuid } from "@ant-design/plots/es/utils";
import { Button, Dropdown, Space, Tag } from "antd";
import { DotVerticalIcon } from "assets/icons";
import moment from "moment/moment";

import AppButton from "../../../components/button";
import ModalForm from "../../../components/forms/components/modalForm";
import EditorQuill from "../../../components/inputs/editorQuill";
import AppTable from "../../../components/table";
import { STATUS_TYPE, USER_TYPE } from "../../../constants/appConstant";
import { AuthInfo } from "../../../stores/slices/auth";
import {
  getIsRefresh,
  setHeaderTitle,
  setIsRefresh,
  setSubHeaderTitle,
} from "../../../stores/slices/layout";
import {
  doDeleteNotice,
  doGetNoticePage,
  numOfEls,
} from "../../../stores/slices/notice";

import NoticeForm from "./noticeForm";

const NoticeBoardPage = () => {
  const { t } = useTranslation();

  const noOfElements = useSelector(numOfEls);
  const authInfo = useSelector(AuthInfo);
  const isRefresh = useSelector(getIsRefresh);

  const dispatch = useDispatch();
  const [isView, setIsView] = useState(false);
  const [isOpenFrm, setIsOpenFrm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [noticeSelected, setNoticeSelected] = useState(null);
  const [columns, setColumns] = useState([]);

  const [searchParam, setSearchParam] = useState();
  const [loadingList, setLoadingList] = useState(false);
  // const [workspaceList, setWorkspaceList] = useState([]);
  const [isOpenModalDelete, setIsOpenModalDelete] = useState(false);
  const [selectedNoticeDelete, setSelectedNoticeDelete] = useState();

  const { workspaceId } = useParams();
  const location = useLocation();

  const handleSearch = (selectedKeys, confirm) => {
    confirm();
  };

  const handleFilter = (selectedKeys, confirm) => {
    confirm();
  };

  const handleTableChange = (pagination, filters, sorter) => {
    let newSearchParam = {
      workspaceId:
        authInfo.type === USER_TYPE.SUPER_ADMIN ? authInfo.workspaceId : null,
    };
    filters.workspaceName &&
      (newSearchParam = {
        ...newSearchParam,
        workspaceName: filters.workspaceName[0],
      });

    filters.notice &&
      (newSearchParam = {
        ...newSearchParam,
        columns: ["notice"],
        searchKeyNotices: filters.notice[0],
      });
    filters.displayFor &&
      filters.displayFor.indexOf("ADMIN") !== -1 &&
      (newSearchParam = { ...newSearchParam, workspaceAdmin: true });
    filters.displayFor &&
      filters.displayFor.indexOf("OPERATOR") !== -1 &&
      (newSearchParam = { ...newSearchParam, workspaceOperator: true });

    filters.startTime &&
      filters.startTime[0] &&
      (newSearchParam = {
        ...newSearchParam,
        fromStartTime: moment(filters.startTime[0].toString()).format(
          "MM/DD/YYYY"
        ),
      });
    filters.startTime &&
      filters.startTime[1] &&
      (newSearchParam = {
        ...newSearchParam,
        toStartTime: moment(filters.startTime[1].toString()).format(
          "MM/DD/YYYY"
        ),
      });

    filters.endTime &&
      filters.endTime[0] &&
      (newSearchParam = {
        ...newSearchParam,
        fromEndTime: moment(filters.endTime[0].toString()).format("MM/DD/YYYY"),
      });
    filters.endTime &&
      filters.endTime[1] &&
      (newSearchParam = {
        ...newSearchParam,
        toEndTime: moment(filters.endTime[1].toString()).format("MM/DD/YYYY"),
      });

    newSearchParam = {
      ...newSearchParam,
      statuses: filters.status ? filters.status : [STATUS_TYPE.ACTIVE],
    };

    if (sorter && sorter.columnKey && sorter.order) {
      newSearchParam = {
        ...newSearchParam,
        sortFieldNotices: sorter.columnKey,
        sortDirection: sorter.order === "ascend" ? "ASC" : "DESC",
      };
    } else {
      newSearchParam = {
        ...newSearchParam,
        sortFieldNotices: "createdTime",
        sortDirection: "DESC",
      };
    }

    newSearchParam = {
      ...newSearchParam,
      pageNumber: pagination.current,
      pageSize: pagination.pageSize,
    };

    setSearchParam(newSearchParam);
  };

  const handleFormatDateLocale = date => {
    const browserLocale = navigator.language;
    let dateLocale;
    if (date) {
      dateLocale = moment(date).utc().locale(browserLocale);
      return `${dateLocale.format("YYYY-MM-DD")} ${dateLocale.format("LT")}`;
    }
  };

  const actionOptionItems = record => {
    return [
      {
        key: "1",
        label: <span>{t("noticeBoard.viewAndEdit")}</span>,
        icon: <EyeOutlined style={{ fontSize: 20 }} />,
        onClick: () => {
          setIsView(true);
          setNoticeSelected(record);
          setIsOpenFrm(true);
        },
      },

      {
        key: "2",
        danger: true,
        label: <span>{t("noticeBoard.deleteNoticeTitle")}</span>,
        icon: <DeleteOutlined style={{ fontSize: 20 }} />,
        onClick: () => {
          setSelectedNoticeDelete(record);
          setIsOpenModalDelete(true);
        },
      },
    ];
  };

  const generateTableColumns = () => {
    let cols = [];
    if (authInfo.type === USER_TYPE.SUPER_ADMIN) {
      cols.push({
        title: t("user.workspaceName"),
        width: 100,
        dataIndex: ["workspace", "name"],
        key: "workspaceName",
        sorter: true,
        isSearchable: true,
        searchPlaceholder:
          "Search " + t("user.workspaceName").toString().toLowerCase(),
        ellipsis: {
          showTitle: true,
        },
        render: (_, { workspace }) => (
          <>{workspace?.name ? workspace.name : "-"}</>
        ),
      });
    }
    cols = [
      ...cols,
      {
        title: t("noticeBoard.notice"),
        width: 120,
        dataIndex: "notice",
        key: "notice",
        sorter: true,
        isSearchable: true,
        ellipsis: {
          showTitle: false,
        },
        render: notice => (
          <EditorQuill
            readOnly={true}
            customStyle={{
              maxHeight: "100px",
              whiteSpace: "initial",
              overflow: "auto",
            }}
            customClassName="customScrollbar borderNone"
            value={notice}
          />
        ),
      },
      {
        title: t("noticeBoard.displayFor"),
        width: 60,
        dataIndex: "displayFor",
        key: "displayFor",
        filters: [
          {
            text: t("role.admin"),
            value: "ADMIN",
          },
          {
            text: t("role.operator"),
            value: "OPERATOR",
          },
        ],
        render: (_, record) => (
          <>
            {record.workspaceAdmin && (
              <Tag color={"success"} bordered={false}>
                {"admin".toUpperCase()}
              </Tag>
            )}
            {record.workspaceOperator && (
              <Tag color={"warning"} bordered={false}>
                {"operator".toUpperCase()}
              </Tag>
            )}
          </>
        ),
      },
      {
        title: t("noticeBoard.startDateAndTime"),
        width: 60,
        dataIndex: "startTime",
        key: "startTime",
        sorter: true,
        isFilterable: true,
        render: (_, { startTime }) => (
          // <>{moment(startTime).utc().format("MM/DD/YYYY HH:mm:ss")}</>
          <>{handleFormatDateLocale(startTime)}</>
        ),
      },
      {
        title: t("noticeBoard.endDateAndTime"),
        width: 60,
        dataIndex: "endTime",
        key: "endTime",
        sorter: true,
        isFilterable: true,
        render: (_, { endTime }) => (
          // <>{moment(endTime).utc().format("MM/DD/YYYY HH:mm:ss")}</>
          <>{handleFormatDateLocale(endTime)}</>
        ),
      },
      {
        title: t("noticeBoard.status"),
        width: 30,
        dataIndex: "status",
        key: "status",
        render: (_, { status }) => (
          <>
            <Tag
              color={status === "ACTIVE" ? "#0E9154" : "#A9122B"}
              key={status}
              icon={status === "ACTIVE" ? <CheckOutlined /> : <CloseOutlined />}
            >
              {status.toUpperCase()}
            </Tag>
          </>
        ),
      },
      {
        title: t("noticeBoard.actions"),
        key: "operation",
        width: 25,
        render: (_, record) => {
          return (
            <>
              <Space size="small">
                {/* <Button
                  type="text"
                  shape="circle"
                  size="middle"
                  onClick={() => {
                    setIsView(true);
                    setNoticeSelected(record);
                    setIsOpenFrm(true);
                  }}
                >
                  <EyeOutlined />
                </Button>

                <Button
                  type="text"
                  shape="circle"
                  size="middle"
                  onClick={() => {
                    setIsView(false);
                    setNoticeSelected(record);
                    setIsOpenFrm(true);
                  }}
                >
                  <EditOutlined />
                </Button>

                <Button
                  type="text"
                  shape="circle"
                  size="middle"
                  danger
                  onClick={() => {
                    setSelectedNoticeDelete(record);
                    setIsOpenModalDelete(true);
                  }}
                >
                  <DeleteOutlined />
                </Button> */}
                <Dropdown
                  trigger={["click"]}
                  menu={{
                    items: actionOptionItems(record),
                  }}
                  placement="bottomRight"
                >
                  <Button type="text" shape="circle" size="middle">
                    <DotVerticalIcon />
                  </Button>
                </Dropdown>
              </Space>
            </>
          );
        },
      },
    ];
    setColumns(cols);
  };

  const handleDelete = async record => {
    const { payload } = await dispatch(doDeleteNotice(record.id));
    setSelectedNoticeDelete();
    setIsOpenModalDelete(false);
    if (payload && payload.status === 200) {
      if (searchParam.pageNumber > 1 && noOfElements === 1) {
        const newSearchParam = {
          ...searchParam,
          pageNumber: searchParam.pageNumber - 1,
        };
        setSearchParam(newSearchParam);
      }

      dispatch(setIsRefresh(uuid()));
      setIsDeleting(false);
    } else {
      setIsDeleting(false);
    }
  };

  useEffect(() => {
    if (location.state && location.state.name) {
      dispatch(setHeaderTitle(["noticeBoard", "workspace"]));
      dispatch(setSubHeaderTitle(location.state.name));
    } else {
      dispatch(setHeaderTitle("noticeBoard"));
    }
  }, []);

  useEffect(() => {
    if (authInfo && authInfo.id) {
      setSearchParam({
        sortFieldNotices: "createdTime",
        sortDirection: "DESC",
        pageNumber: 1,
        pageSize: 10,
        statuses: [STATUS_TYPE.ACTIVE],
        workspaceId:
          authInfo.type === USER_TYPE.SUPER_ADMIN
            ? authInfo.workspaceId || workspaceId
            : workspaceId
            ? workspaceId
            : null,
      });
    }
  }, [authInfo]);

  useEffect(() => {
    generateTableColumns();
  }, [noOfElements]);

  const handleTitleDelete = () => {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
        }}
      >
        <QuestionCircleOutlined style={{ color: "red", fontSize: 24 }} />
        <span style={{ marginLeft: 10, fontSize: 16 }}>
          {t("noticeBoard.deleteNoticeTitle")}
        </span>
      </div>
    );
  };

  return (
    <div style={noticeStyles.mainContainer}>
      <div style={noticeStyles.buttonContainer}>
        <AppButton
          label={t("button.add")}
          color={"primary"}
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            setIsView(false);
            setNoticeSelected(null);
            setIsOpenFrm(true);
          }}
          style={noticeStyles.buttonAdd}
        />
      </div>
      <AppTable
        api={doGetNoticePage(searchParam)}
        columns={columns}
        searchParam={searchParam}
        handleSearch={handleSearch}
        handleFilter={handleFilter}
        isRefresh={isRefresh}
        handleTableChange={handleTableChange}
        setSearchParam={setSearchParam}
      />

      <ModalForm
        title={
          noticeSelected && !isView
            ? t("noticeBoard.updateNotice")
            : noticeSelected && isView
            ? t("noticeBoard.viewNotice")
            : t("noticeBoard.addNewNotice")
        }
        onCancel={() => setIsOpenFrm(false)}
        destroyOnClose={true}
        isOpen={isOpenFrm}
        labelSubmit={t("label.save")}
        customWidth={500}
        footer={false}
        maskClosable={false}
      >
        <NoticeForm
          isViewFrm={isView}
          onUpdate={() => {
            setIsView(false);
          }}
          noticeSelected={noticeSelected}
          loadingList={loadingList}
          setLoadingList={setLoadingList}
          onCancel={() => {
            setNoticeSelected(null);
            setIsOpenFrm(false);
          }}
          onSuccess={() => {
            dispatch(setIsRefresh(uuid()));
            setIsOpenFrm(false);
          }}
        />
      </ModalForm>
      <ModalForm
        isOpen={isOpenModalDelete}
        type="confirm"
        title={handleTitleDelete()}
        onCancel={() => {
          setIsOpenModalDelete(false);
        }}
        onSubmit={() => {
          handleDelete(selectedNoticeDelete);
        }}
        okType={"danger"}
        danger={true}
        maskClosable={false}
        footer={[
          <AppButton
            key="button"
            label={t("button.cancel")}
            color={"grey"}
            loading={isDeleting}
            disabled={isDeleting}
            htmlType="button"
            onClick={() => {
              setIsOpenModalDelete(false);
            }}
          />,
          <AppButton
            key="button"
            color="red"
            label={t("button.delete")}
            loading={isDeleting}
            disabled={isDeleting}
            onClick={() => {
              setIsDeleting(true);
              handleDelete(selectedNoticeDelete);
            }}
          />,
        ]}
      >
        <div style={{ height: 50, marginLeft: 35, fontSize: 14 }}>
          {t("noticeBoard.deleteNoticeMsg")}
        </div>
      </ModalForm>
    </div>
  );
};

const noticeStyles = {
  mainContainer: {
    display: "flex",
    flexDirection: "column",
    // gap: "24px",
    marginBottom: "24px",
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "flex-end",
  },
  buttonAdd: {
    minWidth: "auto",
    marginBottom: 24,
  },
};

export default NoticeBoardPage;
